import {
  Button,
  Card,
  DateRangePicker,
  Divider,
  RadixTable,
  TColumn,
} from '@features/ui';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DateValueType, formatDate } from '@lib/utils.ts';
import { TExpiryDateUser, TReportBlacklistUsersParams } from '@src/types';
import Pagination from '@features/ui/components/pagination.tsx';
import { getUserExpiringEarly } from '@src/services/reports.ts';
import { expiryDateUsers } from '@src/services/users.ts';
import { useNavigate } from 'react-router-dom';

export const ActiveUsersExpiringBeforeAccountExpiry = () => {
  const [valueStart, setValueStart] = useState<DateValueType>(null);
  const [valueEnd, setValueEnd] = useState<DateValueType>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [data, setData] = useState<TExpiryDateUser[]>([]);

  const navigate = useNavigate();

  const fetchUserExpiringEarly = async (
    params?: TReportBlacklistUsersParams
  ) => {
    const res = await getUserExpiringEarly(params);

    setTotalPages(res.total_pages);
    setCurrentPage(res.current_page);
    setData(res.data);
  };

  useEffect(() => {
    fetchUserExpiringEarly().then();
  }, []);

  const handleChangePage = async (page: number) => {
    await fetchUserExpiringEarly({
      start_date: valueStart?.startDate
        ? format(valueStart.startDate, 'yyyy-MM-dd')
        : null,
      end_date: valueEnd?.startDate
        ? format(valueEnd.startDate, 'yyyy-MM-dd')
        : null,
      page,
    });
  };

  const handleGetData = useCallback(async () => {
    await fetchUserExpiringEarly({
      start_date: valueStart?.startDate
        ? format(valueStart.startDate, 'yyyy-MM-dd')
        : null,
      end_date: valueEnd?.startDate
        ? format(valueEnd.startDate, 'yyyy-MM-dd')
        : null,
      page: 1,
    });
  }, [valueEnd?.startDate, valueStart?.startDate]);

  const handleExpiryDateUsers = useCallback(
    async (ids: number[]) => {
      await expiryDateUsers(ids);
      await handleGetData();
    },
    [handleGetData]
  );

  const columns = useMemo(
    (): TColumn<TExpiryDateUser>[] => [
      {
        title: 'Username',
        render: (item) => <p>{item?.username}</p>,
      },
      {
        title: 'Name',
        render: (item) => (
          <p>
            {item?.user.first_name} {item?.user.last_name}
          </p>
        ),
      },
      {
        title: 'Email',
        render: (item) => <p>{item?.user.email}</p>,
      },
      {
        title: 'User Expiry',
        render: (item) => <p>{formatDate(item?.expire_date)}</p>,
      },
      {
        title: 'Account Expiry',
        render: (item) => <p>{formatDate(item?.account_valid_to)}</p>,
      },
      {
        title: 'Accounts',
        render: (item) => (
          <p>
            Active: {item?.active ?? 0} | Closed: {item?.closed ?? 0}
          </p>
        ),
      },
      {
        title: '',
        render: (item) => (
          <div className={'flex items-center gap-2'}>
            <Button
              size={'sm'}
              variant={'lime'}
              onClick={() =>
                item?.id ? handleExpiryDateUsers([item?.id]) : undefined
              }
            >
              Update
            </Button>
            <Button
              size={'sm'}
              variant={'secondary'}
              onClick={() => navigate(`/admin/users/${item?.id}`)}
            >
              Edit
            </Button>
          </div>
        ),
      },
    ],
    [navigate, handleExpiryDateUsers]
  );

  return (
    <div>
      <div className={'flex flex-col gap-7.5'}>
        <div className={'text-xs flex flex-col gap-x-8 gap-y-3'}>
          <h1
            className={'text-[23px]'}
          >{`Active Users expiring before account expiry`}</h1>
          {data?.length ? (
            <p>
              <b>{data.length} user(s)</b> have active accounts expiring after
              their user expiry. To update expiry for ALL or these users in
              RITHMIC click the button below.
            </p>
          ) : null}
        </div>
        {data?.length ? (
          <Button
            className={'w-fit'}
            size={'sm'}
            variant={'lime'}
            onClick={() => handleExpiryDateUsers(data.map((item) => item.id))}
          >
            UPDATE EXPIRY DATE FOR ALL {data.length} USER(S)
          </Button>
        ) : null}
      </div>
      <Card className={'mt-4.5'}>
        <div
          className={
            'flex justify-between items-start lg:flex-row flex-col mb-5 gap-6'
          }
        >
          <div className={'w-full flex gap-6 flex-wrap'}>
            <DateRangePicker
              asSingle={true}
              className={'max-w-[239px]'}
              placeholder={'Created From'}
              size={'sm'}
              value={valueStart}
              variant={'outline'}
              onChange={setValueStart}
            />
            <DateRangePicker
              asSingle={true}
              className={'max-w-[239px]'}
              placeholder={'Created To'}
              size={'sm'}
              value={valueEnd}
              variant={'outline'}
              onChange={setValueEnd}
            />
          </div>
          <div className={'flex gap-5'}>
            <Button size={'sm'} variant={'lime'} onClick={handleGetData}>
              Get
            </Button>
          </div>
        </div>
        <Divider />
        <RadixTable columns={columns} data={data} />
        {totalPages !== 0 ? (
          <div className={'w-full flex justify-end'}>
            <Pagination
              current={currentPage}
              total={totalPages}
              onChange={handleChangePage}
            />
          </div>
        ) : null}
      </Card>
    </div>
  );
};
